import { FC } from 'react';
import intl from 'react-intl-universal';
import { Checkbox } from '@getsynapse/design-system';
import {
  ProjectsListFilters,
  DateRangeFilter as DateRangeFilterType,
} from 'types/store/projectsList';
import {
  ProjectHealth,
  ProjectPriority,
  ProjectPrivacy,
  ProjectResourcingType,
  ProjectStatus,
} from 'utils/types/projectsList';
import ProjectOwnersFilter from './ProjectOwnersFilter/ProjectOwnersFilter';
import ProjectStatusFilter from './ProjectStatusFilter/ProjectStatusFilter';
import ProjectTeamsFilter from './ProjectTeamsFilter/ProjectTeamsFilter';
import ProjectBusinessUnitFilter from './ProjectBusinessUnitFilter/ProjectBusinessUnitFilter';
import DateRangeFilter from './DateRangeFilter/DateRangeFilter';
import ProjectPriorityFilter from './ProjectPriorityFilter/ProjectPriorityFilter';
import ProjectHealthFilter from './ProjectHealthFilter/ProjectHealthFilter';
import ProjectPrivacyFilter from './ProjectPrivacyFilter/ProjectPrivacyFilter';
import ProjectResourcingTypeFilter from './ProjectResourcingTypeFilter/ProjectResourcingTypeFilter';
import ProjectProcessFilter from './ProjectProcessFilter/ProjectProcessFilter';
import ProjectCategoriesFilter from './ProjectCategoriesFilter/ProjectCategoriesFilter';
import ProjectStageFilter from './ProjectStageFilter/ProjectStageFilter';
import ProjectProgramFilter from './ProjectProgramFilter/ProjectProgramFilter';

interface ProjectsTableFiltersFormProps {
  updateFilters: (filters: ProjectsListFilters) => void;
  appliedFilters?: ProjectsListFilters;
  isConsolidatedView?: boolean;
}

const ProjectsTableFiltersForm: FC<ProjectsTableFiltersFormProps> = ({
  updateFilters,
  appliedFilters,
  isConsolidatedView = false,
}) => {
  return (
    <div className='flex flex-col space-y-4 mt-8'>
      {isConsolidatedView && (
        <Checkbox
          label={intl.get('PROJECT_DETAIL.ARCHIVED')}
          onChange={(event) =>
            updateFilters({
              isArchived: event.target.checked ? true : undefined,
            })
          }
          checked={appliedFilters?.isArchived ? true : false}
        />
      )}
      <ProjectStatusFilter
        selected={
          appliedFilters?.status
            ? (appliedFilters.status as ProjectStatus[])
            : []
        }
        onSelect={(selected: ProjectStatus[]) => {
          updateFilters({ status: selected });
        }}
        isConsolidatedView={isConsolidatedView}
      />

      {isConsolidatedView && (
        <ProjectPriorityFilter
          selected={
            appliedFilters?.priority
              ? (appliedFilters.priority as ProjectPriority[])
              : []
          }
          onSelect={(selected: ProjectPriority[]) => {
            updateFilters({ priority: selected });
          }}
        />
      )}

      <ProjectOwnersFilter
        selected={
          appliedFilters?.owners ? (appliedFilters.owners as string[]) : []
        }
        onSelect={(selected: string[]) => {
          updateFilters({ owners: selected });
        }}
      />

      {isConsolidatedView && (
        <ProjectHealthFilter
          selected={
            appliedFilters?.health
              ? (appliedFilters.health as ProjectHealth[])
              : []
          }
          onSelect={(selected: ProjectHealth[]) => {
            updateFilters({ health: selected });
          }}
        />
      )}

      {isConsolidatedView && (
        <ProjectBusinessUnitFilter
          selected={
            appliedFilters?.businessTeams
              ? (appliedFilters.businessTeams as string[])
              : []
          }
          onSelect={(selected: string[]) => {
            updateFilters({ businessTeams: selected });
          }}
        />
      )}

      {isConsolidatedView && (
        <ProjectProgramFilter
          selected={
            appliedFilters?.programs
              ? (appliedFilters.programs as string[])
              : []
          }
          withoutPrograms={appliedFilters?.withoutPrograms === true}
          onSelect={(selected: string[]) => {
            updateFilters({ programs: selected });
          }}
          onSelectWithoutPrograms={(selected: boolean) => {
            updateFilters({ withoutPrograms: selected || undefined });
          }}
        />
      )}

      {isConsolidatedView && (
        <ProjectPrivacyFilter
          selected={
            appliedFilters?.privacy
              ? (appliedFilters.privacy as ProjectPrivacy[])
              : []
          }
          onSelect={(selected: ProjectPrivacy[]) => {
            updateFilters({ privacy: selected });
          }}
        />
      )}

      {isConsolidatedView && (
        <ProjectTeamsFilter
          selected={
            appliedFilters?.teams ? (appliedFilters.teams as string[]) : []
          }
          onSelect={(selected: string[]) => {
            updateFilters({ teams: selected });
          }}
          appliedPrivacyFilters={
            appliedFilters?.privacy ? (appliedFilters.privacy as string[]) : []
          }
          updateAppliedPrivacyFilters={(privacy: string[]) => {
            updateFilters({ privacy });
          }}
        />
      )}

      {isConsolidatedView && (
        <ProjectCategoriesFilter
          selected={
            appliedFilters?.category
              ? (appliedFilters.category as string[])
              : []
          }
          onSelect={(selected: string[]) => {
            updateFilters({ category: selected });
          }}
        />
      )}

      {isConsolidatedView && (
        <DateRangeFilter
          label={intl.get('PROJECT_DETAIL.START_DATE')}
          selected={
            appliedFilters?.startDate
              ? (appliedFilters.startDate as DateRangeFilterType)
              : undefined
          }
          onSelect={(selected: DateRangeFilterType) => {
            let newSelected: DateRangeFilterType | undefined = { ...selected };
            if (!selected.min && !selected.max) {
              newSelected = undefined;
            }
            updateFilters({ startDate: newSelected });
          }}
        />
      )}

      {isConsolidatedView && (
        <DateRangeFilter
          label={intl.get('PROJECT_DETAIL.END_DATE')}
          selected={
            appliedFilters?.targetCompletionDate
              ? (appliedFilters.targetCompletionDate as DateRangeFilterType)
              : undefined
          }
          onSelect={(selected: DateRangeFilterType) => {
            let newSelected: DateRangeFilterType | undefined = { ...selected };
            if (!selected.min && !selected.max) {
              newSelected = undefined;
            }
            updateFilters({ targetCompletionDate: newSelected });
          }}
        />
      )}
      {isConsolidatedView && (
        <DateRangeFilter
          label={intl.get('PROJECT_DETAIL.ACTUAL_COMPLETION_DATE')}
          selected={
            appliedFilters?.actualCompletionDate
              ? (appliedFilters.actualCompletionDate as DateRangeFilterType)
              : undefined
          }
          onSelect={(selected: DateRangeFilterType) => {
            let newSelected: DateRangeFilterType | undefined = { ...selected };
            if (!selected.min && !selected.max) {
              newSelected = undefined;
            }
            updateFilters({ actualCompletionDate: newSelected });
          }}
        />
      )}
      {isConsolidatedView && (
        <ProjectResourcingTypeFilter
          selected={
            appliedFilters?.resourcingType
              ? (appliedFilters.resourcingType as ProjectResourcingType[])
              : []
          }
          onSelect={(selected: ProjectResourcingType[]) => {
            updateFilters({ resourcingType: selected });
          }}
        />
      )}
      {isConsolidatedView && (
        <ProjectProcessFilter
          selected={
            appliedFilters?.process ? (appliedFilters.process as string[]) : []
          }
          onSelect={(selected: string[]) => {
            updateFilters({ process: selected });
          }}
        />
      )}

      {isConsolidatedView && (
        <ProjectStageFilter
          selected={
            appliedFilters?.stage ? (appliedFilters.stage as string[]) : []
          }
          onSelect={(selected: string[]) => {
            updateFilters({ stage: selected });
          }}
          selectedProcesses={
            appliedFilters?.process ? (appliedFilters.process as string[]) : []
          }
          updateSelectedProcesses={(selected: string[]) => {
            updateFilters({ process: selected });
          }}
        />
      )}
    </div>
  );
};

export default ProjectsTableFiltersForm;
